import React, { useEffect, useState } from "react";
import axios from "axios";
import DashboardLayout from "./DashboardLayout";
import { API_BASE } from "../utils/apiBase";

function NotificationCenterPage() {
  const currentUser = (() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch (error) {
      return {};
    }
  })();
  const resolvedActorId = Number(currentUser?.id || 0) > 0 ? Number(currentUser.id) : 1;
  const adminAuthParams = {
    actor_type: "admin",
    actor_id: resolvedActorId,
  };
  const [audience, setAudience] = useState("all");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [customEmails, setCustomEmails] = useState("");
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateKey, setSelectedTemplateKey] = useState("");
  const [recipients, setRecipients] = useState({ students: [], franchises: [], admins: [] });
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [selectedFranchises, setSelectedFranchises] = useState([]);
  const [selectedAdmins, setSelectedAdmins] = useState([]);
  const [loadingRecipients, setLoadingRecipients] = useState(false);
  const [recipientError, setRecipientError] = useState("");

  useEffect(() => {
    const loadRecipients = async () => {
      setLoadingRecipients(true);
      setRecipientError("");
      try {
        const res = await axios.get(`${API_BASE}/notification-recipients`, { params: adminAuthParams });
        setRecipients(res.data || {});
      } catch (error) {
        try {
          // Fallback for master admin sessions where stored id is 0.
          const res = await axios.get(`${API_BASE}/notification-recipients`, {
            params: { actor_type: "admin", actor_id: 1 },
          });
          setRecipients(res.data || {});
        } catch {
          setRecipients({ students: [], franchises: [], admins: [] });
          setRecipientError("Recipients could not be loaded. Please refresh and try again.");
        }
      } finally {
        setLoadingRecipients(false);
      }
    };
    loadRecipients();
  }, []);

  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const res = await axios.get(`${API_BASE}/notification-settings`, { params: adminAuthParams });
        setTemplates(Array.isArray(res.data?.templates) ? res.data.templates : []);
      } catch {
        try {
          const res = await axios.get(`${API_BASE}/notification-settings`, {
            params: { actor_type: "admin", actor_id: 1 },
          });
          setTemplates(Array.isArray(res.data?.templates) ? res.data.templates : []);
        } catch {
          setTemplates([]);
        }
      }
    };
    loadTemplates();
  }, []);

  const toggleId = (setter, current, id) => {
    if (current.includes(id)) setter(current.filter((x) => x !== id));
    else setter([...current, id]);
  };

  const send = async () => {
    const payload = {
      ...adminAuthParams,
      audience,
      subject,
      body,
      custom_emails: customEmails,
      selected_students: selectedStudents,
      selected_franchises: selectedFranchises,
      selected_admins: selectedAdmins,
    };
    const res = await axios.post(`${API_BASE}/notification-send`, payload);
    alert(res.data?.message || "Notification sent");
  };

  const applyTemplate = () => {
    const item = (templates || []).find((t) => t.key === selectedTemplateKey);
    if (!item) return;
    setSubject(item.subject || "");
    setBody(item.body || "");
  };

  return (
    <DashboardLayout>
      <div className="container-fluid">
        <h3 className="mb-3">Notification Panel</h3>
        <div className="row g-3">
          <div className="col-md-8">
            <label className="form-label">Message Template</label>
            <div className="d-flex gap-2">
              <select className="form-control" value={selectedTemplateKey} onChange={(e) => setSelectedTemplateKey(e.target.value)}>
                <option value="">Select template</option>
                {(templates || []).map((item) => (
                  <option key={item.key} value={item.key}>{item.key}</option>
                ))}
              </select>
              <button type="button" className="btn btn-outline-primary" onClick={applyTemplate} disabled={!selectedTemplateKey}>
                Apply
              </button>
            </div>
          </div>
          <div className="col-md-4">
            <label className="form-label">Audience</label>
            <select className="form-control" value={audience} onChange={(e) => setAudience(e.target.value)}>
              <option value="all">All</option>
              <option value="students">Students</option>
              <option value="franchises">Franchises</option>
              <option value="admins">Admins</option>
              <option value="custom">Custom Emails</option>
              <option value="selected">Selected (Part by Part)</option>
            </select>
          </div>
          <div className="col-md-8">
            <label className="form-label">Subject</label>
            <input className="form-control" value={subject} onChange={(e) => setSubject(e.target.value)} />
          </div>
          <div className="col-12">
            <label className="form-label">Message (HTML supported)</label>
            <textarea className="form-control" rows={6} value={body} onChange={(e) => setBody(e.target.value)} />
          </div>
          {audience === "custom" && (
            <div className="col-12">
              <label className="form-label">Custom Emails (comma separated)</label>
              <input className="form-control" value={customEmails} onChange={(e) => setCustomEmails(e.target.value)} />
            </div>
          )}
        </div>
        {recipientError ? <div className="alert alert-danger mt-3 mb-0">{recipientError}</div> : null}

        {audience === "selected" && (
          <div className="row g-3 mt-2">
            <div className="col-md-4">
              <h6>Students</h6>
              <div style={{ maxHeight: 220, overflowY: "auto", border: "1px solid #ddd", padding: 8 }}>
                {loadingRecipients ? <div className="small text-muted">Loading recipients...</div> : null}
                {(recipients.students || []).map((r) => (
                  <label key={`s-${r.id}`} className="d-block small">
                    <input type="checkbox" className="me-2" checked={selectedStudents.includes(r.id)} onChange={() => toggleId(setSelectedStudents, selectedStudents, r.id)} />
                    {r.name} ({r.email})
                  </label>
                ))}
              </div>
            </div>
            <div className="col-md-4">
              <h6>Franchises</h6>
              <div style={{ maxHeight: 220, overflowY: "auto", border: "1px solid #ddd", padding: 8 }}>
                {loadingRecipients ? <div className="small text-muted">Loading recipients...</div> : null}
                {(recipients.franchises || []).map((r) => (
                  <label key={`f-${r.id}`} className="d-block small">
                    <input type="checkbox" className="me-2" checked={selectedFranchises.includes(r.id)} onChange={() => toggleId(setSelectedFranchises, selectedFranchises, r.id)} />
                    {r.name} ({r.email})
                  </label>
                ))}
              </div>
            </div>
            <div className="col-md-4">
              <h6>Admins</h6>
              <div style={{ maxHeight: 220, overflowY: "auto", border: "1px solid #ddd", padding: 8 }}>
                {loadingRecipients ? <div className="small text-muted">Loading recipients...</div> : null}
                {(recipients.admins || []).map((r) => (
                  <label key={`a-${r.id}`} className="d-block small">
                    <input type="checkbox" className="me-2" checked={selectedAdmins.includes(r.id)} onChange={() => toggleId(setSelectedAdmins, selectedAdmins, r.id)} />
                    {r.name} ({r.email})
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}

        <button className="btn btn-primary mt-3" onClick={send}>Send Notification</button>
      </div>
    </DashboardLayout>
  );
}

export default NotificationCenterPage;




